import React, { useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Chip, useTheme, useMediaQuery } from "@mui/material";

import { Header, BackButton } from "../../components";
import TicketChat from "./components/TicketChat";
import { useLoader } from "../../context/loaderContext";
import { useSocketContext } from "../../context/SocketContext";
import { getTicketDetailById, sendTicketMessage } from "services/clientService";
import {
  formatTicketDate,
  getTicketPriorityColor,
  getTicketStatusColor,
  ticketStatus,
} from "./components/action";
import { markAllTicketNoficationsAsRead } from "../../services/clientService";

const TicketDetailPage = () => {
  const { socket, currentTicket, setCurrentTicket } = useSocketContext();
  const { showLoader, hideLoader } = useLoader();

  const { id: ticketId } = useParams();

  const navigate = useNavigate();
  const { palette, ...theme } = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchTicketDetail = async () => {
    const res = await getTicketDetailById(showLoader, hideLoader, ticketId);

    setCurrentTicket(res ?? { id: ticketId });

    // Mark all notifications as read
    await markAllTicketNoficationsAsRead();
  };

  useEffect(() => {
    fetchTicketDetail();

    return () => {
      // Cleanup function to reset the current ticket when the component unmounts
      setCurrentTicket({});
    };
  }, [ticketId]);

  // Handle sending a new message
  const handleSendMessage = useCallback(async (content, attachment) => {
    try {
      // Create FormData object for Update server
      const formData = new FormData();

      formData.append("message", content);
      // Append image if exists
      if (attachment && attachment.file) {
        // Use the correct field name that the server expects in req.files.image
        // The key name here is important - it must match what the server expects
        formData.append("image", attachment.file, attachment.file.name);
      }

      await sendTicketMessage(ticketId, formData); // chat body will be updated in from the socket context
    } catch (error) {
      console.log("Error on updating chat in server ", error);
    }
  }, []);

  useEffect(() => {
    // Chat box is filled using socket context, so need to join the ticket room
    if (!socket || !ticketId) {
      return;
    }

    if (socket.connected) {
      socket.emit("join-ticket", ticketId);
    } else {
      socket.once("connect", () => {
        socket.emit("join-ticket", ticketId);
      });
    }

    return () => {
      socket.emit("leave-ticket", ticketId);
    };
  }, [socket, ticketId]);

  const OtherDetail = ({ text, children }) => (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexWrap: "nowrap",
        justifyContent: "space-between",
        width: "200px",
        alignItems: "center",
      }}
    >
      <Typography variant="body2" sx={{ color: "#aaa" }}>
        {text}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Header>
      <Box
        sx={{
          height: isMobile ? "calc(100vh - 76px)" : "calc(100vh - 170px)",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          pt: isMobile ? 2 : 8,
          pb: 10,
          gap: "32px",
        }}
      >
        {/* Back button */}
        <Box
          sx={{
            width: "95px",
            height: "26px",
            position: "sticky",
            top: 0,
            left: isMobile ? "20px" : "111px",
            zIndex: 1,
            mb: 2,
          }}
        >
          {!isMobile && <BackButton onClick={() => navigate(-1)} />}
        </Box>

        {/* Main content container */}
        <Box
          sx={{
            width: "95vw",
            maxWidth: "1620px",
            display: "flex",
            flexDirection: "column",
            mx: "auto",
            px: isMobile ? 2 : 4,
            position: "sticky",
          }}
        >
          <Typography
            sx={{
              textTransform: "capitalize",
              fontFamily: "var(--font-mz_r)",
              alignItems: "center",
              color: palette.grey[200],
              fontSize: {
                xl: "1.5vw",
                lg: "1.5vw",
                md: "36px",
                sm: "36px",
                xs: "36px",
              },
            }}
          >
            Ticket {currentTicket?.id}
          </Typography>

          {/* Ticket header */}
          <Box
            sx={{
              mb: 3,
              backgroundColor: "#111",
              p: 3,
              borderRadius: "16px",
              border: "1px solid #474747",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <Typography variant="h5" sx={{ color: "#B1B1B1" }}>
              {currentTicket?.title}
            </Typography>
            <Typography variant="p" sx={{ color: "#B1B1B1" }}>
              {currentTicket?.description}
            </Typography>
            <Box sx={{ display: "flex", gap: "32px", flexWrap: "wrap" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <OtherDetail text="Status">
                  <Chip
                    label={
                      ticketStatus.find(
                        (item) => item.id === currentTicket?.status
                      )?.name ?? ""
                    }
                    color={getTicketStatusColor(currentTicket?.status)}
                    size="small"
                  />
                </OtherDetail>
                <OtherDetail text="Priority">
                  <Typography
                    color={getTicketPriorityColor(currentTicket?.priority)}
                    fontWeight="medium"
                  >
                    {currentTicket?.priority}
                  </Typography>
                </OtherDetail>
                <OtherDetail text="Category">
                  <Typography variant="p" sx={{ color: "#B1B1B1" }}>
                    {currentTicket?.category}
                  </Typography>
                </OtherDetail>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <Typography variant="body2" sx={{ color: "#aaa" }}>
                  Created: {formatTicketDate(currentTicket?.createdAt)}
                </Typography>
                <Typography variant="body2" sx={{ color: "#aaa" }}>
                  Last Updated: {formatTicketDate(currentTicket?.updatedAt)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Chat component */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            paddingBottom: "50px",
            height: "100%",
            width: "95vw",
            maxWidth: "1620px",
            px: isMobile ? 2 : 4,
            mx: "auto",
          }}
        >
          <TicketChat
            messages={currentTicket?.messages || []}
            onSendMessage={handleSendMessage}
            ticketStatus={currentTicket?.status ?? "open"}
          />
        </Box>
      </Box>
    </Header>
  );
};

export default TicketDetailPage;
